import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { DateInput } from 'semantic-ui-calendar-react'
import { Form, Table } from 'semantic-ui-react'
import StockExChangeService from '~/api/services/stockExchange'
import { FormFields } from '~/components/FormFields'
import { getError } from '~/components/FormFields/util'
import Layout from '~/components/Layout'
import notify from '~/components/Notify'
import { useAuth } from '~/routes/components/AuthProvider'
import { RouteTypes } from '~/routes/constants'
import { formatNumber, handleErrors, parseDate } from '~/utils/helpers'

const CreateEditStoneXBle: React.FC = () => {
  const { t } = useTranslation()
  const { systemData: { languages, logged: { languageId } } } = useAuth()
  const languageLocal = languages.find(language => language.id === languageId)
  const { id } = useParams()
  const navigate = useNavigate()

  const { control, handleSubmit, setValue, formState: { isSubmitted }, reset, clearErrors } = useForm({
    mode: 'onSubmit'
  })

  const watchConclusionDate = useWatch({ control, name: 'contractConclusionDate' })
  const watchAppliedOn = useWatch({ control, name: 'appliedOn' })
  const watchTradingSlot = useWatch({ control, name: 'tradingSlotId' })
  const watchSetlementPrice = useWatch({ control, name: 'settlementPrice' })
  const watchTradingMonth = useWatch({ control, name: 'tradingMonth' })

  const [isBusy, setIsBusy] = useState(false)
  const [options, setOptions] = useState(null)
  const [isFetchingOptions, setIsFetchingOptions] = useState(false)

  async function fetchOptions(tradingMonth?) {
    setIsFetchingOptions(true)
    try {
      const response = await StockExChangeService.getStoneXContractsOptions({ tradingMonth })
      setOptions(response.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsFetchingOptions(false)
  }

  useEffect(() => {
    fetchOptions()
  }, [])

  useEffect(() => {
    if (id) {
      fetchData()
    }
  }, [id])

  async function fetchData() {
    setIsBusy(true)
    try {
      const response = await StockExChangeService.getStoneXContract(id)
      response.data.contractConclusionDate = dayjs(response.data.contractConclusionDate).format('DD.MM.YYYY')
      response.data.appliedOn = dayjs(response.data.appliedOn).format('DD.MM.YYYY')
      reset(response.data)
      if (response.data.tradingMonth) {
        await fetchOptions(response.data.tradingMonth)
      }
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  async function onSubmit(data) {
    setIsBusy(true)
    data.settlementPrice = data?.settlementPrice?.replace(',', '.')
    data.cashAmount = data?.cashAmount?.replace(',', '.')
    data.appliedOn = parseDate(data.appliedOn)
    data.contractConclusionDate = parseDate(data.contractConclusionDate)
    try {
      if (id) {
        await StockExChangeService.updateStoneXContract(id, data)
        notify(t('Successfully updated BLE price contract'), 'success')
      } else {
        await StockExChangeService.storeStoneXContract(data)
        notify(t('Successfully created BLE price contract'), 'success')
      }
      navigate(RouteTypes.DAIRY_FACTORY_STONE_X_BLE)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  const selectedTradingSlot = options?.tradingSlots?.find(tradingSlot => tradingSlot?.id === watchTradingSlot)
  const settlementPrice = watchSetlementPrice?.replace(',', '.') || 0
  const settlementVolume = parseFloat(settlementPrice) * parseInt(selectedTradingSlot?.contractAmount || 0)

  function renderForm() {
    return <div style={{ padding: 20 }}>
      <Form.Group widths={'equal'}>
        <FormFields.Select
          placeholder='Handelsmonat'
          control={control}
          label={'Handelsmonat'}
          loading={isFetchingOptions}
          disabled={isFetchingOptions}
          name={'tradingMonth'}
          onChange={(_, { value }) => {
            setValue('tradingMonth', value)
            setValue('appliedOn', '')
            setValue('contractConclusionDate', '')
            setValue('tradingSlotId', '')
            clearErrors(['tradingMonth', 'appliedOn', 'contractConclusionDate', 'tradingSlotId'])
            fetchOptions(value)
          }}
          options={options?.months?.map(month => ({ value: month.key, text: month.label }))}
          rules={{
            required: true
          }}
          noErrorMessage
        />
        <FormFields.Select
          placeholder='Handels-Slot'
          control={control}
          label={'Handels-Slot'}
          loading={isFetchingOptions}
          disabled={isFetchingOptions}
          name={'tradingSlotId'}
          options={options?.tradingSlots?.map(tradingSlot => ({ value: tradingSlot.id, text: `${tradingSlot.uniqueId} (${formatNumber(tradingSlot?.contractVolume || 0, 0)})` }))}
          rules={{
            required: true
          }}
          noErrorMessage
        />
        <FormFields.Input
          control={control}
          label={'Kontrakt-ID'}
          placeholder={'Kontrakt-ID'}
          name={'contractId'}
          rules={{
            required: true,
            maxLength: {
              value: 100,
              message: t('Max 100 char.')
            }
          }}
        />
      </Form.Group>
      <Form.Group widths={'equal'}>
        <Controller
          control={control}
          name={'contractConclusionDate'}
          rules={{
            required: true
          }}
          render={({ field, fieldState }) => {
            const minDate = dayjs(watchTradingMonth)
              .startOf('month')
              .format('DD.MM.YYYY');
            const maxDate = watchTradingMonth ? dayjs(watchTradingMonth).endOf('month').format('DD.MM.YYYY') : undefined
            return <Form.Field
              required
              key={watchTradingMonth || 'no-month'}
              error={!watchConclusionDate && isSubmitted ? getError(fieldState) : false}
            >
              <label>{'Kontraktabschluss'}</label>
              <DateInput
                name='date'
                key={watchTradingMonth || 'no-month'}
                placeholder={'Kontraktabschluss'}
                disabled={!watchTradingMonth}
                initialDate={minDate}
                maxDate={maxDate}
                closable
                onKeyDown={(event) => {
                  event.preventDefault();
                }}
                markColor={'blue'}
                localization={languageLocal.code}
                closeOnMouseLeave={false}
                dateFormat={'DD.MM.YYYY'}
                className={'datePicker'}
                value={watchConclusionDate || ''}
                iconPosition='left'
                onChange={(event, { name, value }) => setValue('contractConclusionDate', value)}
              />
            </Form.Field>

          }}
        />
        <Controller
          control={control}
          name={'appliedOn'}
          rules={{
            required: true
          }}
          render={({ field, fieldState }) => {
            const initialDate = dayjs(watchTradingMonth).format('DD.MM.YYYY');
            return <Form.Field
              required
              key={`applied-${watchTradingMonth}` || 'no-month'}
              error={!watchAppliedOn && isSubmitted ? getError(fieldState) : false}
            >
              <label>{'Glattstellung'}</label>
              <DateInput
                name='date'
                key={`applied-${watchTradingMonth}` || 'no-month'}
                placeholder={'Glattstellung'}
                closable
                onKeyDown={(event) => {
                  event.preventDefault();
                }}
                markColor={'blue'}
                initialDate={initialDate}
                minDate={initialDate}
                disabled={!watchTradingMonth}
                localization={languageLocal.code}
                closeOnMouseLeave={false}
                dateFormat={'DD.MM.YYYY'}
                className={'datePicker'}
                value={watchAppliedOn || ''}
                iconPosition='left'
                onChange={(event, { name, value }) => setValue('appliedOn', value)}
              />
            </Form.Field>

          }}
        />
      </Form.Group>
      <Form.Group widths={'equal'}>
        <FormFields.Input
          control={control}
          label={'Settlement Kurs (in €/kg)'}
          placeholder={'Settlement Kurs (in €/kg)'}
          typeInput={'controled'}
          allowDecimals
          decimalScale={4}
          suffix=' €/kg'
          noErrorMessage
          name={'settlementPrice'}
          rules={{
            required: true,
            validate: (value) => {
              const valueTransf = parseFloat(value?.replace(',', '.') || 0)
              if (valueTransf < 0.0001 || valueTransf > 0.9999) {
                return t('Min value is 0.0001 and max is 0.9999')
              }
              return true
            }
          }}
        />

        <FormFields.Input
          control={control}
          label={'Cash Amount (in €)'}
          noErrorMessage
          placeholder={'Cash Amount (in €)'}
          typeInput={'controled'}
          decimalScale={2}
          allowDecimals
          suffix=' €'
          name={'cashAmount'}
          rules={{
            required: true
          }}
        />

      </Form.Group>
      <div style={{ marginTop: '2em' }}>
        <Table compact celled>
          <Table.Body>
            <Table.Row>
              <Table.Cell>Anzahl Kontrakte</Table.Cell>
              <Table.Cell textAlign='right'>
                {selectedTradingSlot?.numberOfContracts || 0}
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>Kontraktmenge (in kg)</Table.Cell>
              <Table.Cell textAlign='right'>
                {`${formatNumber(selectedTradingSlot?.contractAmount || 0, 0)} kg`}
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>Kontraktpreis (in €/kg)</Table.Cell>
              <Table.Cell textAlign='right'>
                {`${formatNumber(selectedTradingSlot?.contractPrice || 0, 4)} €/kg`}
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>Kontraktvolumen (in €)</Table.Cell>
              <Table.Cell textAlign='right'>
                {`${formatNumber(selectedTradingSlot?.contractVolume || 0, 2)} €`}
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>Settlement Volumen (in €)</Table.Cell>
              <Table.Cell textAlign='right'>
                {`${formatNumber(settlementVolume, 2)} €`}
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>Gebühren (in €)</Table.Cell>
              <Table.Cell textAlign='right'>
                {`${formatNumber(selectedTradingSlot?.fees || 0, 2)} €`}
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </div>
    </div>
  }

  return <Layout
    onSubmit={handleSubmit(onSubmit)}
    isBusy={isBusy}
    action={id ? t('Update') : t('Save')}
    title={t('Create StoneX BLE Cash S.')}
    close={RouteTypes.DAIRY_FACTORY_STONE_X_BLE}
    segmented
  >
    {renderForm()}
  </Layout>
}
export default CreateEditStoneXBle